import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select, SelectTrigger, SelectContent, SelectItem } from '../ui/Select';
import { X, Plus, X as XIcon } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';

interface TicketCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated: () => void;
}

const TicketCreateModal: React.FC<TicketCreateModalProps> = ({
  isOpen,
  onClose,
  onTicketCreated
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium', // Valor interno em inglês para API
    category: 'other', // Valor interno em inglês para API
    tags: [] as string[]
  });
  const [newTag, setNewTag] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Estados para controlar a exibição dos labels
  const [selectedPriorityLabel, setSelectedPriorityLabel] = useState('Média');
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState('Outro');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Atualizar labels de exibição
    if (field === 'priority') {
      setSelectedPriorityLabel(getPriorityLabel(value));
    } else if (field === 'category') {
      setSelectedCategoryLabel(getCategoryLabel(value));
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Incluir dados do usuário logado no ticket
      const ticketData = {
        ...formData,
        createdBy: {
          _id: user?.id,
          username: user?.username,
          email: user?.email,
          name: user?.name || user?.username,
          role: user?.level || 'user'
        }
      };
      
      // Usar apiService em vez de fetch direto
      await apiService.createTicket(ticketData);

      // Reset form
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        category: 'other',
        tags: []
      });
      setNewTag('');
      
      onTicketCreated();
      onClose();
    } catch (error) {
      console.error('Erro ao criar ticket:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      category: 'other',
      tags: []
    });
    setNewTag('');
    setSelectedPriorityLabel('Média');
    setSelectedCategoryLabel('Outro');
    onClose();
  };

  // Função para obter label da prioridade
  const getPriorityLabel = (priority: string) => {
    const labels = {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta',
      urgent: 'Urgente'
    };
    return labels[priority as keyof typeof labels] || priority;
  };

  // Função para obter label da categoria
  const getCategoryLabel = (category: string) => {
    const labels = {
      technical: 'Técnico',
      billing: 'Faturamento',
      account: 'Conta',
      feature_request: 'Solicitação de Funcionalidade',
      bug_report: 'Relatório de Bug',
      other: 'Outro'
    };
    return labels[category as keyof typeof labels] || category;
  };


  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Novo Ticket</span>
            <Button variant="outline" size="lg" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Título */}
          <div className="space-y-2">
            <label className="text-lg font-medium text-gray-700">
              Título <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Digite o título do ticket"
              required
            />
          </div>

          {/* Categoria e Prioridade */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-lg font-medium text-gray-700">Categoria</label>
              <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                <SelectTrigger>
                  <span className="text-gray-900">{selectedCategoryLabel}</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technical">Técnico</SelectItem>
                  <SelectItem value="billing">Faturamento</SelectItem>
                  <SelectItem value="account">Conta</SelectItem>
                  <SelectItem value="feature_request">Solicitação de Funcionalidade</SelectItem>
                  <SelectItem value="bug_report">Relatório de Bug</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Prioridade</label>
              <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                <SelectTrigger>
                  <span className="text-gray-900">{selectedPriorityLabel}</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Tags</label>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Digite uma tag"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button
                type="button"
                onClick={handleAddTag}
                variant="outline"
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Adicionar
              </Button>
            </div>
            
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-blue-600"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Descrição <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Descreva detalhadamente o problema ou solicitação"
              rows={6}
              required
            />
            <p className="text-xs text-gray-500">
              Seja específico e inclua todos os detalhes relevantes para que possamos ajudá-lo melhor.
            </p>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!formData.title.trim() || !formData.description.trim() || isLoading}
            >
              {isLoading ? 'Criando...' : 'Criar Ticket'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TicketCreateModal;
